/**
 * Capa de cribado para extracción de reportes EMG/NCS.
 * Valida coherencia interna, cruce nombre-archivo vs contenido, y rangos fisiológicos.
 */

import { ExtractedDocument, ExtractedNCSFinding } from '../types/extraction';
import {
  extractPatientFromFilename,
  extractPatientFromTables,
  extractPatientFromText,
  mergePatientHeaders,
  XltekPatientHeader
} from './xltekTemplateParser';

export type ScreeningSeverity = 'error' | 'warning' | 'info';

export interface ScreeningIssue {
  id: string;
  severity: ScreeningSeverity;
  category: 'patient' | 'ncs' | 'extraction' | 'filename';
  message: string;
  field?: string;
  requiresReview: boolean;
}

export interface ExtractionScreeningResult {
  issues: ScreeningIssue[];
  patientHeader: XltekPatientHeader;
  filenamePatient: ReturnType<typeof extractPatientFromFilename>;
  overallConfidence: number;
  requiresHumanReview: boolean;
  screenedFindings: ExtractedNCSFinding[];
}

const PHYSIO_LIMITS = {
  motor: { latency: [1, 30], amplitude: [0, 30], velocity: [10, 90] },
  sensory: { latency: [0.5, 15], amplitude: [0, 200], velocity: [10, 80] }
};

function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function namesMatch(a: string, b: string): boolean {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;

  const wordsA = na.split(' ').filter(w => w.length > 2);
  const wordsB = nb.split(' ').filter(w => w.length > 2);
  const overlap = wordsA.filter(w => wordsB.includes(w));
  return overlap.length >= Math.min(2, Math.min(wordsA.length, wordsB.length));
}

function screenPatientCrossValidation(
  header: XltekPatientHeader,
  filenamePatient: ReturnType<typeof extractPatientFromFilename>,
  issues: ScreeningIssue[]
): void {
  if (!header.name && !filenamePatient.name) {
    issues.push({
      id: 'missing-patient-name',
      severity: 'warning',
      category: 'patient',
      message: 'No se detectó nombre del paciente en el documento ni en el archivo.',
      field: 'name',
      requiresReview: true
    });
    return;
  }

  if (header.name && filenamePatient.name && !namesMatch(header.name, filenamePatient.name)) {
    issues.push({
      id: 'filename-name-mismatch',
      severity: 'error',
      category: 'filename',
      message: `Nombre en archivo ("${filenamePatient.name}") no coincide con contenido ("${header.name}"). Verifique que el reporte corresponde al paciente correcto.`,
      field: 'name',
      requiresReview: true
    });
  }

  if (header.studyDate && filenamePatient.studyDate) {
    const normalizeDate = (d: string) => d.replace(/[./-]/g, '');
    if (normalizeDate(header.studyDate) !== normalizeDate(filenamePatient.studyDate)) {
      issues.push({
        id: 'filename-date-mismatch',
        severity: 'warning',
        category: 'filename',
        message: `Fecha en archivo (${filenamePatient.studyDate}) difiere de Study Date (${header.studyDate}).`,
        field: 'studyDate',
        requiresReview: true
      });
    }
  }
}

function screenNCSFinding(finding: ExtractedNCSFinding, index: number): ScreeningIssue[] {
  const issues: ScreeningIssue[] = [];
  const prefix = `ncs-${index}`;

  if (finding.side === 'unknown') {
    issues.push({
      id: `${prefix}-unknown-side`,
      severity: 'warning',
      category: 'ncs',
      message: `${finding.nerve}: lado no determinado.`,
      field: 'side',
      requiresReview: true
    });
  }

  if (finding.absentResponse && finding.amplitude?.value && finding.amplitude.value > 0) {
    issues.push({
      id: `${prefix}-absent-with-amplitude`,
      severity: 'error',
      category: 'ncs',
      message: `${finding.nerve} ${finding.side}: marcado como sin respuesta pero tiene amplitud numérica (${finding.amplitude.value}).`,
      requiresReview: true
    });
  }

  const limits = finding.type === 'motor' ? PHYSIO_LIMITS.motor : PHYSIO_LIMITS.sensory;

  if (finding.latency?.value !== undefined) {
    const [min, max] = limits.latency;
    if (finding.latency.value < min || finding.latency.value > max) {
      issues.push({
        id: `${prefix}-latency-out-of-range`,
        severity: 'warning',
        category: 'ncs',
        message: `${finding.nerve}: latencia ${finding.latency.value} ms fuera de rango fisiológico (${min}-${max} ms).`,
        requiresReview: true
      });
    }
  }

  if (finding.velocity?.value !== undefined) {
    const [min, max] = limits.velocity;
    if (finding.velocity.value < min || finding.velocity.value > max) {
      issues.push({
        id: `${prefix}-velocity-out-of-range`,
        severity: 'warning',
        category: 'ncs',
        message: `${finding.nerve}: velocidad ${finding.velocity.value} m/s fuera de rango (${min}-${max} m/s).`,
        requiresReview: true
      });
    }
  }

  if (finding.confidence < 0.6 || finding.requiresReview) {
    issues.push({
      id: `${prefix}-low-confidence`,
      severity: 'info',
      category: 'ncs',
      message: `${finding.nerve} ${finding.side}: confianza baja (${(finding.confidence * 100).toFixed(0)}%) — requiere revisión.`,
      requiresReview: true
    });
  }

  return issues;
}

function screenDocumentQuality(document: ExtractedDocument, issues: ScreeningIssue[]): void {
  if (document.tables.length === 0) {
    issues.push({
      id: 'no-tables-extracted',
      severity: 'warning',
      category: 'extraction',
      message: 'No se extrajeron tablas del documento. Los datos numéricos pueden estar incompletos.',
      requiresReview: true
    });
  }

  if (document.plainText.length < 200) {
    issues.push({
      id: 'short-text',
      severity: 'warning',
      category: 'extraction',
      message: `Texto extraído corto (${document.plainText.length} caracteres).`,
      requiresReview: true
    });
  }

  document.warnings.forEach((warning, i) => {
    issues.push({
      id: `extract-warning-${i}`,
      severity: 'info',
      category: 'extraction',
      message: warning,
      requiresReview: false
    });
  });
}

export function screenExtraction(
  document: ExtractedDocument,
  ncsFindings: ExtractedNCSFinding[] = []
): ExtractionScreeningResult {
  const issues: ScreeningIssue[] = [];

  const fromTables = extractPatientFromTables(document.tables);
  const fromText = extractPatientFromText(document.plainText);
  const patientHeader = mergePatientHeaders(fromTables, fromText);
  const filenamePatient = extractPatientFromFilename(document.sourceFileName);

  screenPatientCrossValidation(patientHeader, filenamePatient, issues);
  screenDocumentQuality(document, issues);

  const screenedFindings = ncsFindings.map(finding => ({ ...finding }));
  screenedFindings.forEach((finding, index) => {
    const findingIssues = screenNCSFinding(finding, index);
    issues.push(...findingIssues);
    if (findingIssues.some(i => i.requiresReview)) {
      finding.requiresReview = true;
    }
  });

  const errorCount = issues.filter(i => i.severity === 'error').length;
  const reviewCount = issues.filter(i => i.requiresReview).length;
  const requiresHumanReview = errorCount > 0 || reviewCount >= 3;

  const extractionConfidence = Math.min(
    0.95,
    patientHeader.confidence * 0.4 +
      (document.tables.length > 0 ? 0.3 : 0.1) +
      (ncsFindings.length > 0 ? 0.2 : 0) +
      (issues.filter(i => i.severity === 'error').length === 0 ? 0.1 : 0)
  );

  return {
    issues,
    patientHeader,
    filenamePatient,
    overallConfidence: extractionConfidence,
    requiresHumanReview,
    screenedFindings
  };
}
